import { Button, cn } from "@beweegbaas/ui";
import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";
import { HistoryBackLink } from "#/components/back-link";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { Page } from "#/components/page";
import { TabsNavigation } from "#/components/tabs-navigation";
import { RapportInstellingenDialog } from "#/modules/rapporten/components/card.rapport-preferences";
import { getRapportStep } from "#/modules/rapporten/rapporten-flow.steps";
import { RapportenFlowSummary } from "#/modules/rapporten/rapporten-flow-summary";
import { useRapportenFlow } from "#/modules/rapporten/use-rapporten-flow";
import { m } from "#/paraglide/messages";

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/reports/flow/$id/step/3",
)({
  component: ReportsStep3Layout,
  errorComponent: ReportsStep3LayoutError,
});

function ReportsStep3Layout() {
  const params = Route.useParams();
  const { rapport } = useRapportenFlow();
  const navigate = useNavigate();
  const hasMultipleKlassen = rapport?.klassen.length && rapport.klassen.length > 1;

  return (
    <Page>
      <Page.Header>
        <HistoryBackLink
          className={cn("max-md:self-start max-sm:mb-4")}
          fallback={{ from: Route.fullPath, to: "../2" }}
        />
        <Page.Title>{m["rapporten.notities.title"]()}</Page.Title>

        <Page.Header.Actions className="gap-2">
          <Page.Header.SecondaryActions>
            <RapportInstellingenDialog />
          </Page.Header.SecondaryActions>
          <Page.Header.MainAction>
            <Button
              className="max-sm:w-full"
              type={"button"}
              size={"xl"}
              onClick={() => {
                const step4 = getRapportStep({
                  params,
                  rapport,
                  step: 4,
                });
                if (step4) {
                  void navigate(step4.link(rapport));
                }
              }}
            >
              {m["common.actions.next"]()}
              <IconMaterialSymbolsArrowRightAltRounded />
            </Button>
          </Page.Header.MainAction>
        </Page.Header.Actions>
      </Page.Header>
      {hasMultipleKlassen ? (
        <Page.Subnavigation>
          <TabsNavigation className="min-w-0 grow">
            <TabsNavigation.List>
              {rapport?.klassen?.map((klas) => (
                <TabsNavigation.Link
                  key={klas.id}
                  to="/$locale/{-$schoolyear}/$school/reports/flow/$id/step/3/$class"
                  params={{ ...params, class: klas.id }}
                >
                  Klas {klas.name}
                </TabsNavigation.Link>
              ))}
            </TabsNavigation.List>
          </TabsNavigation>
        </Page.Subnavigation>
      ) : null}
      <Page.Container>
        <Page.Body>
          <Page.SidebarLayout>
            <div className="grow">
              <Outlet />
            </div>

            <Page.Sidebar className="max-lg:hidden">
              <RapportenFlowSummary />
            </Page.Sidebar>
          </Page.SidebarLayout>
        </Page.Body>
      </Page.Container>
    </Page>
  );
}

function ReportsStep3LayoutError({ error }: { error: unknown }) {
  const routeParams = Route.useParams();
  return <ErrorBoundaryComponent error={error} params={routeParams} />;
}
